import { useEffect, useMemo, useState } from 'react';
import type { ChangeEvent } from 'react';
import {
  Alert,
  Box,
  Button,
  Container,
  Divider,
  Grid,
  IconButton,
  MenuItem,
  Paper,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import {
  Add as AddIcon,
  DeleteOutline as DeleteOutlineIcon,
} from '@mui/icons-material';
import { httpsCallable } from 'firebase/functions';
import { ZodError } from 'zod';
import { useNavigate } from 'react-router-dom';
import { functions } from '../firebase';
import { useCustomerOnboarding } from '../hooks/useCustomerOnboarding';
import { useTenant } from '../providers/TenantProvider';
import type { OnboardingStatus, WebsiteOnboarding } from '../lib/db/types';
import { defaultValues, onboardingSchema, type OnboardingFormData } from '../lib/validations/onboarding';

const dayLabels: Array<keyof OnboardingFormData['businessHours']> = [
  'monday',
  'tuesday',
  'wednesday',
  'thursday',
  'friday',
  'saturday',
  'sunday',
];

const timeZoneOptions = [
  'America/New_York',
  'America/Chicago',
  'America/Denver',
  'America/Los_Angeles',
  'America/Phoenix',
  'America/Anchorage',
  'Pacific/Honolulu',
];

function cloneDefaultValues() {
  return JSON.parse(JSON.stringify(defaultValues)) as OnboardingFormData;
}

function mergeOnboardingData(
  onboarding: WebsiteOnboarding | null | undefined,
  fallbackSlug: string
): OnboardingFormData {
  const merged = cloneDefaultValues();
  if (!onboarding) {
    merged.bookingSlug = fallbackSlug;
    return merged;
  }

  return {
    ...merged,
    ...onboarding,
    businessHours: {
      ...merged.businessHours,
      ...onboarding.businessHours,
    },
    location: {
      ...merged.location,
      ...onboarding.location,
    },
    services: onboarding.services?.length
      ? onboarding.services.map((service) => ({
          name: service.name,
          description: service.description || '',
          durationMinutes: service.durationMinutes,
          priceLabel: service.priceLabel || '',
        }))
      : merged.services,
    staff: onboarding.staff?.length
      ? onboarding.staff.map((person) => ({
          name: person.name,
          role: person.role,
          email: person.email || '',
        }))
      : merged.staff,
    bookingSlug: onboarding.bookingSlug || fallbackSlug,
    timeZone: onboarding.timeZone || merged.timeZone,
    status: onboarding.status || 'in_progress',
  };
}

function messageFromError(error: unknown) {
  if (error instanceof ZodError) {
    return error.issues[0]?.message || 'Please review the form details';
  }

  if (error instanceof Error) {
    return error.message;
  }

  return String(error);
}

export function OnboardingPage() {
  const navigate = useNavigate();
  const { activeTenant, roles, tenantId, refreshTenants } = useTenant();
  const { onboarding, loading, error: onboardingError, saveOnboarding } = useCustomerOnboarding(tenantId || '');
  const [form, setForm] = useState<OnboardingFormData>(() => mergeOnboardingData(null, ''));
  const [saving, setSaving] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [message, setMessage] = useState<string | null>(null);

  const completeTenantOnboarding = useMemo(
    () => httpsCallable(functions, 'completeTenantOnboarding'),
    []
  );

  useEffect(() => {
    const fallbackSlug = activeTenant?.slug || '';
    setForm(mergeOnboardingData((onboarding?.data as unknown as WebsiteOnboarding) || null, fallbackSlug));
  }, [activeTenant?.slug, onboarding]);

  const isOwner = roles.includes('owner');
  const billingReady = activeTenant?.billingStatus === 'active' || activeTenant?.billingStatus === 'trialing';

  const handleTopLevelChange = <K extends keyof OnboardingFormData>(key: K, value: OnboardingFormData[K]) => {
    setForm((current) => ({ ...current, [key]: value }));
  };

  const handleBusinessHoursChange = (
    day: keyof OnboardingFormData['businessHours'],
    field: 'opens' | 'closes' | 'closed',
    value: string | boolean
  ) => {
    setForm((current) => ({
      ...current,
      businessHours: {
        ...current.businessHours,
        [day]: {
          ...current.businessHours[day],
          [field]: value,
        },
      },
    }));
  };

  const handleLocationChange = (field: keyof OnboardingFormData['location'], value: string) => {
    setForm((current) => ({
      ...current,
      location: {
        ...current.location,
        [field]: value,
      },
    }));
  };

  const handleServiceChange = (
    index: number,
    field: keyof OnboardingFormData['services'][number],
    value: string | number | undefined
  ) => {
    setForm((current) => ({
      ...current,
      services: current.services.map((service, serviceIndex) => (
        serviceIndex === index ? { ...service, [field]: value } : service
      )),
    }));
  };

  const handleStaffChange = (
    index: number,
    field: keyof OnboardingFormData['staff'][number],
    value: string
  ) => {
    setForm((current) => ({
      ...current,
      staff: current.staff.map((person, staffIndex) => (
        staffIndex === index ? { ...person, [field]: value } : person
      )),
    }));
  };

  const handleSave = async (status: OnboardingStatus = 'in_progress') => {
    if (!tenantId) return;

    setSaving(true);
    setError(null);
    setMessage(null);

    try {
      const parsed = onboardingSchema.parse({ ...form, status });
      await saveOnboarding(parsed, status);
      await refreshTenants();
      setMessage('Onboarding draft saved.');
    } catch (saveError) {
      console.error('Error saving onboarding:', saveError);
      setError(messageFromError(saveError));
    } finally {
      setSaving(false);
    }
  };

  const handleComplete = async () => {
    if (!tenantId) return;

    setSubmitting(true);
    setError(null);
    setMessage(null);

    try {
      const parsed = onboardingSchema.parse({ ...form, status: 'in_progress' });
      await saveOnboarding(parsed, 'in_progress');
      await completeTenantOnboarding({ tenantId });
      await refreshTenants();
      navigate('/dashboard', { replace: true });
    } catch (submitError) {
      console.error('Error completing onboarding:', submitError);
      setError(messageFromError(submitError));
    } finally {
      setSubmitting(false);
    }
  };

  if (!tenantId) {
    return (
      <Box sx={{ minHeight: '100vh', bgcolor: 'grey.50', py: 6 }}>
        <Container maxWidth="sm">
          <Alert severity="warning">A tenant is required before onboarding can begin.</Alert>
        </Container>
      </Box>
    );
  }

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: 'grey.50', py: 4 }}>
      <Container maxWidth="lg">
        <Stack spacing={3}>
          <Box>
            <Typography variant="h4" fontWeight={700} gutterBottom>
              Finish your workspace setup
            </Typography>
            <Typography color="text.secondary">
              We keep activation on the webhook. This page only captures the operational details your team needs once the subscription is active.
            </Typography>
          </Box>

          {!isOwner && <Alert severity="warning">Only tenant owners can complete onboarding.</Alert>}
          {activeTenant && !billingReady && (
            <Alert severity="warning">
              Billing is still pending for <strong>{activeTenant.name}</strong>. Finish checkout before finalizing onboarding.
            </Alert>
          )}
          {onboardingError && <Alert severity="error">{onboardingError.message}</Alert>}
          {error && <Alert severity="error">{error}</Alert>}
          {message && <Alert severity="success">{message}</Alert>}
          {loading && <Alert severity="info">Loading your saved onboarding draft…</Alert>}

          <Paper sx={{ p: 3 }}>
            <Typography variant="h6" fontWeight={700} gutterBottom>
              Business hours
            </Typography>
            <Grid container spacing={2}>
              {dayLabels.map((day) => (
                <Grid item xs={12} md={6} key={day}>
                  <Paper variant="outlined" sx={{ p: 2 }}>
                    <Stack spacing={1.5}>
                      <Typography fontWeight={600} sx={{ textTransform: 'capitalize' }}>
                        {day}
                      </Typography>
                      <Stack direction="row" spacing={1.5}>
                        <TextField
                          fullWidth
                          type="time"
                          label="Opens"
                          InputLabelProps={{ shrink: true }}
                          value={form.businessHours[day].opens}
                          disabled={form.businessHours[day].closed}
                          onChange={(event: ChangeEvent<HTMLInputElement>) =>
                            handleBusinessHoursChange(day, 'opens', event.target.value)
                          }
                        />
                        <TextField
                          fullWidth
                          type="time"
                          label="Closes"
                          InputLabelProps={{ shrink: true }}
                          value={form.businessHours[day].closes}
                          disabled={form.businessHours[day].closed}
                          onChange={(event: ChangeEvent<HTMLInputElement>) =>
                            handleBusinessHoursChange(day, 'closes', event.target.value)
                          }
                        />
                      </Stack>
                      <Button
                        variant={form.businessHours[day].closed ? 'contained' : 'outlined'}
                        onClick={() => handleBusinessHoursChange(day, 'closed', !form.businessHours[day].closed)}
                        sx={{ alignSelf: 'flex-start' }}
                      >
                        {form.businessHours[day].closed ? 'Closed' : 'Open'}
                      </Button>
                    </Stack>
                  </Paper>
                </Grid>
              ))}
            </Grid>
          </Paper>

          <Paper sx={{ p: 3 }}>
            <Typography variant="h6" fontWeight={700} gutterBottom>
              Location and appointment basics
            </Typography>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <TextField
                  select
                  fullWidth
                  label="Time zone"
                  value={form.timeZone}
                  onChange={(event: ChangeEvent<HTMLInputElement>) => handleTopLevelChange('timeZone', event.target.value)}
                >
                  {timeZoneOptions.map((timeZone) => (
                    <MenuItem key={timeZone} value={timeZone}>
                      {timeZone}
                    </MenuItem>
                  ))}
                </TextField>
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Appointment URL slug"
                  value={form.bookingSlug}
                  onChange={(event: ChangeEvent<HTMLInputElement>) => handleTopLevelChange('bookingSlug', event.target.value.toLowerCase())}
                  helperText="Lowercase letters, numbers, and hyphens only."
                />
              </Grid>
              <Grid item xs={12}>
                <TextField fullWidth label="Street address" value={form.location.addressLine1} onChange={(event: ChangeEvent<HTMLInputElement>) => handleLocationChange('addressLine1', event.target.value)} />
              </Grid>
              <Grid item xs={12}>
                <TextField fullWidth label="Address line 2" value={form.location.addressLine2 || ''} onChange={(event: ChangeEvent<HTMLInputElement>) => handleLocationChange('addressLine2', event.target.value)} />
              </Grid>
              <Grid item xs={12} sm={4}>
                <TextField fullWidth label="City" value={form.location.city} onChange={(event: ChangeEvent<HTMLInputElement>) => handleLocationChange('city', event.target.value)} />
              </Grid>
              <Grid item xs={12} sm={4}>
                <TextField fullWidth label="State / region" value={form.location.region} onChange={(event: ChangeEvent<HTMLInputElement>) => handleLocationChange('region', event.target.value)} />
              </Grid>
              <Grid item xs={12} sm={4}>
                <TextField fullWidth label="Postal code" value={form.location.postalCode} onChange={(event: ChangeEvent<HTMLInputElement>) => handleLocationChange('postalCode', event.target.value)} />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField fullWidth label="Country" value={form.location.country} onChange={(event: ChangeEvent<HTMLInputElement>) => handleLocationChange('country', event.target.value)} />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField fullWidth label="Logo URL (optional)" value={form.logo || ''} onChange={(event: ChangeEvent<HTMLInputElement>) => handleTopLevelChange('logo', event.target.value)} />
              </Grid>
            </Grid>
          </Paper>

          <Paper sx={{ p: 3 }}>
            <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
              <Typography variant="h6" fontWeight={700}>
                Services and classes
              </Typography>
              <Button startIcon={<AddIcon />} onClick={() => setForm((current) => ({ ...current, services: [...current.services, { name: '', description: '', durationMinutes: 60, priceLabel: '' }] }))}>
                Add service
              </Button>
            </Stack>
            <Stack spacing={2}>
              {form.services.map((service, index) => (
                <Paper key={`service-${index}`} variant="outlined" sx={{ p: 2 }}>
                  <Grid container spacing={2}>
                    <Grid item xs={12} sm={5}>
                      <TextField fullWidth label="Name" value={service.name} onChange={(event: ChangeEvent<HTMLInputElement>) => handleServiceChange(index, 'name', event.target.value)} />
                    </Grid>
                    <Grid item xs={12} sm={3}>
                      <TextField fullWidth label="Duration (minutes)" type="number" value={service.durationMinutes || ''} onChange={(event: ChangeEvent<HTMLInputElement>) => handleServiceChange(index, 'durationMinutes', Number(event.target.value) || undefined)} />
                    </Grid>
                    <Grid item xs={12} sm={3}>
                      <TextField fullWidth label="Price label" value={service.priceLabel || ''} onChange={(event: ChangeEvent<HTMLInputElement>) => handleServiceChange(index, 'priceLabel', event.target.value)} />
                    </Grid>
                    <Grid item xs={12} sm={1} sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end' }}>
                      <IconButton
                        aria-label="Remove service"
                        onClick={() => setForm((current) => ({ ...current, services: current.services.filter((_, serviceIndex) => serviceIndex !== index) }))}
                        disabled={form.services.length === 1}
                      >
                        <DeleteOutlineIcon />
                      </IconButton>
                    </Grid>
                    <Grid item xs={12}>
                      <TextField fullWidth multiline minRows={2} label="Description" value={service.description || ''} onChange={(event: ChangeEvent<HTMLInputElement>) => handleServiceChange(index, 'description', event.target.value)} />
                    </Grid>
                  </Grid>
                </Paper>
              ))}
            </Stack>
          </Paper>

          <Paper sx={{ p: 3 }}>
            <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
              <Typography variant="h6" fontWeight={700}>
                Staff setup
              </Typography>
              <Button startIcon={<AddIcon />} onClick={() => setForm((current) => ({ ...current, staff: [...current.staff, { name: '', role: '', email: '' }] }))}>
                Add staff
              </Button>
            </Stack>
            <Stack spacing={2}>
              {form.staff.map((person, index) => (
                <Paper key={`staff-${index}`} variant="outlined" sx={{ p: 2 }}>
                  <Grid container spacing={2}>
                    <Grid item xs={12} sm={4}>
                      <TextField fullWidth label="Name" value={person.name} onChange={(event: ChangeEvent<HTMLInputElement>) => handleStaffChange(index, 'name', event.target.value)} />
                    </Grid>
                    <Grid item xs={12} sm={4}>
                      <TextField fullWidth label="Role" value={person.role} onChange={(event: ChangeEvent<HTMLInputElement>) => handleStaffChange(index, 'role', event.target.value)} />
                    </Grid>
                    <Grid item xs={12} sm={3}>
                      <TextField fullWidth label="Email (optional)" value={person.email || ''} onChange={(event: ChangeEvent<HTMLInputElement>) => handleStaffChange(index, 'email', event.target.value)} />
                    </Grid>
                    <Grid item xs={12} sm={1} sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end' }}>
                      <IconButton
                        aria-label="Remove staff member"
                        onClick={() => setForm((current) => ({ ...current, staff: current.staff.filter((_, staffIndex) => staffIndex !== index) }))}
                        disabled={form.staff.length === 1}
                      >
                        <DeleteOutlineIcon />
                      </IconButton>
                    </Grid>
                  </Grid>
                </Paper>
              ))}
            </Stack>
          </Paper>

          <Paper sx={{ p: 3 }}>
            <Typography variant="h6" fontWeight={700} gutterBottom>
              Policies
            </Typography>
            <TextField
              fullWidth
              multiline
              minRows={4}
              label="Cancellation policy"
              value={form.cancellationPolicy}
              onChange={(event: ChangeEvent<HTMLInputElement>) => handleTopLevelChange('cancellationPolicy', event.target.value)}
            />
          </Paper>

          <Divider />

          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} justifyContent="space-between">
            <Button variant="outlined" disabled={!isOwner || saving} onClick={() => void handleSave('in_progress')}>
              {saving ? 'Saving...' : 'Save draft'}
            </Button>
            <Button
              variant="contained"
              disabled={!isOwner || !billingReady || submitting}
              onClick={() => void handleComplete()}
            >
              {submitting ? 'Finalizing...' : 'Complete onboarding'}
            </Button>
          </Stack>
        </Stack>
      </Container>
    </Box>
  );
}

export default OnboardingPage;
